import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import SearchSelect from '../../components/SearchSelect';
import { fetchEmployees, ROLE_LABELS } from '../../services/employees';
import ImpersonatedApp, { DESKTOP_ROLES, homeFor, useImpersonateFetch } from './impersonation';

const LOGICAL_W = 1440;
const LOGICAL_H = 900;

export default function DesktopPreview() {
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState(''); // '' = your own Executive view
  const [nonce, setNonce] = useState(0);             // bump to reset/re-mount the inner app
  const [scale, setScale] = useState(1);
  const frameRef = useRef(null);        // measured for the fit-to-width scale
  const hostRef = useRef(null);         // the createRoot mount point
  const impersonateRef = useRef(null);  // current user id to view-as (read by the fetch shim)
  const [searchParams] = useSearchParams();

  useImpersonateFetch(impersonateRef);

  // Load the real employee list — same source as the Employees page — and keep only
  // the roles that actually have a desktop layout.
  useEffect(() => {
    fetchEmployees()
      .then(list => {
        const desk = (Array.isArray(list) ? list : [])
          .filter(u => DESKTOP_ROLES.includes(u.role) && (u.status ? u.status === 'active' : true))
          .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
        setPeople(desk);
        // Deep-link: /admin/desktop-preview?as=<userId>.
        const as = searchParams.get('as');
        if (as && desk.some(u => String(u.id) === String(as))) { setSelectedId(as); setNonce(n => n + 1); }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const person = people.find(p => String(p.id) === String(selectedId)) || null;
  const role = person ? person.role : 'Executive';
  const startPath = homeFor(role);
  const roleLabel = ROLE_LABELS[role] || role;

  // Fit the 1440-wide desktop into whatever width the page gives us.
  useEffect(() => {
    function fit() { if (frameRef.current) setScale(Math.min(1, frameRef.current.clientWidth / LOGICAL_W)); }
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => { impersonateRef.current = person ? String(person.id) : null; }, [person]);

  // Render the preview into its own React root. A fresh host node per run keeps this
  // safe under StrictMode's double-invoke (same pattern as Mobile Preview).
  useEffect(() => {
    if (!hostRef.current) return;
    impersonateRef.current = person ? String(person.id) : null;
    const node = document.createElement('div');
    node.style.cssText = `width:${LOGICAL_W}px;height:${LOGICAL_H}px;overflow:hidden`;
    hostRef.current.appendChild(node);
    const root = createRoot(node);
    root.render(<ImpersonatedApp role={role} startPath={startPath} />);
    return () => { setTimeout(() => { try { root.unmount(); } catch {} node.remove(); }, 0); };
  }, [selectedId, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Desktop Preview</h1>
          <p className="admin-page-sub">See a teammate's desktop exactly as they see it — their menus, dashboard, and their real data — without logging in as them. <strong>Read-only:</strong> nothing you click here can change their data.</p>
        </div>
      </div>

      {/* Person picker */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>View as:</span>
        <div style={{ minWidth: 280 }}>
          <SearchSelect value={selectedId} onChange={v => { setSelectedId(v); setNonce(n => n + 1); }} placeholder="Type a teammate's name…"
            options={[
              { value: '', label: 'You (Executive)', sub: 'your own view' },
              ...people.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}`, sub: ROLE_LABELS[u.role] || u.role })),
            ]} />
        </div>
        <button onClick={() => setNonce(n => n + 1)} title="Reload this person's home screen"
          style={{ padding: '9px 14px', borderRadius: 20, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ↻ Reset to Home
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>
        Only office roles (Executive, Project Manager, Office/Accounting) have a desktop layout. Field &amp; shop crew are mobile-only — use <strong>Mobile Preview</strong> for those.
      </div>

      {/* Browser frame — scaled to fit */}
      <div ref={frameRef} style={{ paddingBottom: 24 }}>
        <div style={{ width: LOGICAL_W * scale, maxWidth: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid #d1d5db', boxShadow: '0 18px 50px rgba(0,0,0,0.22)', background: '#fff' }}>
          {/* Fake browser chrome */}
          <div style={{ height: 38, background: '#e8eaed', display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderBottom: '1px solid #d1d5db' }}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f57' }} />
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#febc2e' }} />
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28c840' }} />
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '4px 16px', fontSize: 12, color: '#374151', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {person
                  ? <>👁 Viewing as <strong>{person.firstName} {person.lastName}</strong> · {roleLabel} · live data (read-only)</>
                  : <>app.titanelectricreno.com — your Executive view</>}
              </div>
            </div>
          </div>
          {/* Scaled viewport */}
          <div style={{ width: LOGICAL_W * scale, height: LOGICAL_H * scale, overflow: 'hidden' }}>
            <div ref={hostRef} style={{ width: LOGICAL_W, height: LOGICAL_H, transform: `scale(${scale})`, transformOrigin: 'top left' }} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
